import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { roles } from '../team-management/roles';
import { environment } from 'src/environments/environment';

interface MenuItem {
  title: string;
  url: string;
  icon?: string;
  photo?: string;
  state?: { [key: string]: any };
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  // company logo is base64 string
  private menuCompanyLogo = new BehaviorSubject<string>("");
  menuCompanyLogo$ = this.menuCompanyLogo.asObservable();

  private menuItemsSource = new BehaviorSubject<MenuItem[]>([]);
  menuItems$ = this.menuItemsSource.asObservable();

  private moduleItems = new Map<string, MenuItem>();

  // Menu of the active plan/role, kept so a team member restriction can narrow it
  // instead of rebuilding from the classic enterprise menu and dropping entries
  // that only exist in other plan menus (ARS, Organigramm, Chat, ...).
  private activeRoleMenu: MenuItem[] = [];
  private roleRestrictionUrls: string[] | null = null;

  // Only URLs a team member role actually governs may be removed by a restriction.
  private static readonly roleGovernedUrls: Set<string> = new Set(
    roles.reduce<string[]>((urls, role) => urls.concat(role.availableMenuPoints), [])
  );

  public userRole: string = 'ToBeSet'; // Default to 'ToBeSet' until properly initialized
  userRole$ = new BehaviorSubject<string>(this.userRole);

  // Safety counter to prevent infinite loops
  private updateCounter = 0;
  private readonly MAX_UPDATE_DEPTH = 5;

  public planLimits = {};
  planLimits$ = new BehaviorSubject<any>(this.planLimits);

  private sidebarToggleSource = new Subject<void>();
  sidebarToggle$ = this.sidebarToggleSource.asObservable();

  toggleSidebar(): void {
    this.sidebarToggleSource.next();
  }

  private sidebarCollapsedSource = new BehaviorSubject<boolean>(MenuService.readCollapsedFlag());
  sidebarCollapsed$ = this.sidebarCollapsedSource.asObservable();

  private static readCollapsedFlag(): boolean {
    try {
      return localStorage.getItem('menuIconsOnly') === 'true';
    } catch {
      return false;
    }
  }

  // Published by AppComponent whenever the icons-only menu state is applied, so pages can
  // mirror the collapsed state without polling localStorage or listening for resize events.
  setSidebarCollapsed(collapsed: boolean): void {
    if (this.sidebarCollapsedSource.value !== collapsed) {
      this.sidebarCollapsedSource.next(collapsed);
    }
  }

  // Quick Wins guide popup events
  private quickWinsGuideSource = new Subject<{ title: string; message: string }>();
  quickWinsGuide$ = this.quickWinsGuideSource.asObservable();

  private organizationPopupSource = new Subject<{ route: string; title: string; message: string }>();
  organizationPopup$ = this.organizationPopupSource.asObservable();

  public AccountSettings = [
    {
      title: 'Profil',
      url: '/profile',
      photo: '../assets/flaticons/profile.png',
    },
  ];
  AccountSettings$ = new BehaviorSubject<any>(this.AccountSettings);

  // Free users menu
  private freeUserMenu: MenuItem[] = [
    // { title: 'Dashboard', url: '/dashboard', photo: '../assets/icon/dashboard.png' },
    // { title: 'Insights', url: '/insights', photo: '../assets/flaticons/insights.png' },
    // { title: 'Einstellungen', url: '/settings', photo: '../assets/flaticons/settings.png' },
  ];

  // Enterprise users menu
  private enterpriseUserMenu: MenuItem[] = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      // photo: '../assets/flaticons_white/dashboard.png',
      icon: 'home',
    },
    {
      title: 'Interviews',
      url: '/job-offers',
      // photo: '../assets/flaticons_white/loupe.png',
      icon: 'search',
    },
    {
      title: 'Kandidaten',
      url: '/applicants',
      // photo: '../assets/flaticons_white/candidates.png',
      icon: 'people',
    },
    {
      title: 'Vertrieb',
      url: '/mona-communication-list',
      // photo: '../assets/flaticons_white/sales.png',
      icon: 'stats-chart',
    },
    // {
    //   title: 'Standort-Teams',
    //   url: '/location-teams',
    //   icon: 'location',
    // },
    // {
    //   title: 'Organigramm',
    //   url: '/org-builder',
    //   photo: '../assets/flaticons_white/manager.png',
    // },
    // {
    //   title: 'Process Manager',
    //   url: '/workflow-builder',
    //   icon: 'git-branch',
    // },
    // { title: 'CRM', url: '/crm', photo: '../assets/flaticons_white/crm.png' },
    // {
    //   title: 'Matching',
    //   url: '/mona-communication/matching',
    //   photo: '../assets/flaticons_white/matching.png',
    // },
    {
      title: 'Insights',
      url: '/insights',
      // photo: '../assets/flaticons_white/insights.png',
      icon: 'pie-chart',
    },
    {
      title: 'Data Pipeline',
      url: '/data-pipeline',
      icon: 'analytics',
    },
    {
      title: 'MCP',
      url: '/mcp',
      icon: 'extension-puzzle',
    },

    //  {
    //    title: 'Org Builder',
    //    url: '/org-builder',
    //    photo: '../assets/flaticons_white/manager.png',
    //  },
    // {
    //   title: 'StaffingConsultant',
    //   url: '/staffing-consultant',
    //   photo: '../assets/flaticons_white/consultant.png',
    // },
    // { title: 'Einstellungen', url: '/settings', photo: '../assets/flaticons/settings.png' },
  ];

  // Agents users menu
  private agentsUserMenu: MenuItem[] = [
    {
      title: 'Chat',
      url: '/agent-dashboard',
      icon: 'chatbox',
    },
    {
      title: 'Agents',
      url: '/mona-agent-chat',
      icon: 'people',
    },
    {
      title: 'Shortlist',
      url: '/shortlist',
      icon: 'bookmark',
    },
    // {
    //   title: 'Kandidaten',
    //   url: '/applicants',
    //   photo: '../assets/flaticons_white/candidates.png',
    // },
    {
      title: 'Agent Builder',
      url: '/mona-agent-builder',
      icon: 'construct',
    },
    // {
    //   title: 'Process Manager',
    //   url: '/workflow-builder',
    //   icon: 'git-branch',
    // },

    // {
    //   title: 'Standort-Teams',
    //   url: '/location-teams',
    //   icon: 'location',
    // },
    // {
    //   title: 'Organigramm',
    //   url: '/org-builder',
    //   photo: '../assets/flaticons_white/manager.png',
    // },
    {
      title: 'Data Pipeline',
      url: '/data-pipeline',
      icon: 'analytics',
    },
    {
      title: 'MCP',
      url: '/mcp',
      icon: 'extension-puzzle',
    },
    // {
    //   title: 'Email Inbox',
    //   url: '/email-inbox',
    //   photo: '../assets/svg/email.svg',
    // },
    // {
    //   title: 'Database',
    //   url: '/database',
    //   icon: 'server'
    // }
  ];

  public getAgentsMenu(): MenuItem[] {
    return this.withCompanyKnowledge(this.withIntegrations(this.agentsUserMenu));
  }

  public getAgentsEnterpriseMenu(): MenuItem[] {
    return this.withOrganigramm(this.withCompanyKnowledge(this.withIntegrations(this.agentsEnterpriseMenu)));
  }

  // Shared menu for agents-free, agents-pro, agents-enterprise
  private agentsFreeMenu: MenuItem[] = [
    {
      title: 'Chat',
      url: '/agent-dashboard',
      icon: 'chatbox',
    },
    {
      title: 'Kandidaten',
      url: '/applicants',
      photo: '../assets/flaticons_white/candidates.png',
    },
    {
      title: 'Shortlist',
      url: '/shortlist',
      icon: 'bookmark',
    },
    {
      title: 'Agents',
      url: '/mona-agent-chat',
      icon: 'people',
    },
    {
      title: 'Agent Builder',
      url: '/mona-agent-builder',
      icon: 'construct',
    },
    {
      title: 'Data Pipeline',
      url: '/data-pipeline',
      icon: 'analytics',
    },
    {
      title: 'MCP',
      url: '/mcp',
      icon: 'extension-puzzle',
    },
  ];

  // Agents enterprise menu - reuses shared agents tier menu
  private agentsEnterpriseMenu: MenuItem[] = this.agentsFreeMenu;

  // Agents enterprise menu with ARS access for users with hybridAccess enabled
  private agentsEnterpriseHybridMenu: MenuItem[] = [
    ...this.agentsFreeMenu,
    {
      title: 'ARS',
      url: '/hybrid-dashboard',
      icon: 'search',
    },
    {
      title: 'Notetaker',
      url: '/notetaker',
      icon: 'mic',
    },
  ];

  private hybridUserMenu: MenuItem[] = [
    {
      title: 'Home',
      url: '/hybrid-dashboard',
      icon: 'grid',
    },
    {
      title: 'ARS',
      url: '/hybrid-dashboard',
      icon: 'search',
    },
    {
      title: 'Notetaker',
      url: '/notetaker',
      icon: 'mic',
    },
    {
      title: 'Daten Import',
      url: '/upload',
      icon: 'cloud-upload',
    },
    {
      title: 'Stellenanzeigen',
      url: '/external-job-offers',
      icon: 'document-text',
    },
    {
      title: 'MCP',
      url: '/mcp',
      icon: 'extension-puzzle',
    },
  ];

  private enterpriseHybridMenu: MenuItem[] = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      icon: 'home',
    },
    {
      title: 'ARS',
      url: '/hybrid-dashboard',
      icon: 'search',
    },
    {
      title: 'Notetaker',
      url: '/notetaker',
      icon: 'mic',
    },
    {
      title: 'Interviews',
      url: '/job-offers',
      icon: 'briefcase',
    },
    {
      title: 'Kandidaten',
      url: '/applicants',
      icon: 'people',
    },
    {
      title: 'Vertrieb',
      url: '/mona-communication-list',
      icon: 'stats-chart',
    },
    {
      title: 'Insights',
      url: '/insights',
      icon: 'pie-chart',
    },
    {
      title: 'Data Pipeline',
      url: '/data-pipeline',
      icon: 'analytics',
    },
    {
      title: 'MCP',
      url: '/mcp',
      icon: 'extension-puzzle',
    },
  ];

  private selfserviceRecruitingMenu: MenuItem[] = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      photo: '../assets/flaticons_white/dashboard.png',
    },
    {
      title: 'Interviews',
      url: '/job-offers',
      photo: '../assets/flaticons_white/loupe.png',
    },
    {
      title: 'Kandidaten',
      url: '/applicants',
      photo: '../assets/flaticons_white/candidates.png',
    },
    // {
    //   title: 'Kommunikation',
    //   url: '/mona-communication/gptsales',
    //   photo: '../assets/flaticons_white/user.png',
    // },
    // {
    //   title: 'Standort-Teams',
    //   url: '/location-teams',
    //   photo: '../assets/flaticons_white/manager.png',
    // },
    // {
    //   title: 'Data Pipeline',
    //   url: '/data-pipeline',
    //   photo: '../assets/flaticons_white/insights.png',
    // },
    // {
    //   title: 'Email Inbox',
    //   url: '/email-inbox',
    //   photo: '../assets/svg/email.svg',
    // },
    // { title: 'CRM', url: '/crm', photo: '../assets/flaticons_white/crm.png' },
    // {
    //   title: 'Matching',
    //   url: '/mona-communication/matching',
    //   photo: '../assets/flaticons_white/matching.png',
    // },
    {
      title: 'Insights',
      url: '/insights',
      photo: '../assets/flaticons_white/sales.png',
    },
  ];

  private withIntegrations(items: MenuItem[]): MenuItem[] {
    const hasIntegrations = items.some(i => i.url === '/integrations');
    if (hasIntegrations) return items;
    return [
      ...items,
      // {
      //   title: 'Integrationen',
      //   url: '/integrations',
      //   photo: '../assets/flaticons_white/integrations.png'
      // }
    ];
  }

  private withOrganigramm(items: MenuItem[]): MenuItem[] {
    const hasOrg = items.some(i => i.url === '/org-builder');
    if (hasOrg) return items;
    const insertAfter = items.findIndex(i => i.url === '/data-pipeline');
    // The organigram's own page, not the legacy workflow-builder's organigram VIEW. `state` is left
    // in place but is inert here: only workflow-builder.page reads view: 'organigramm', to pick which
    // of its own tabs to open.
    const orgItem: MenuItem = {
      title: 'Organigramm',
      url: '/organigram',
      icon: 'git-network',
      state: { view: 'organigramm' }
    };
    if (insertAfter >= 0) {
      const result = [...items];
      result.splice(insertAfter + 1, 0, orgItem);
      return result;
    }
    return [...items, orgItem];
  }

  private withCompanyKnowledge(items: MenuItem[]): MenuItem[] {
    const hasKnowledge = items.some(i => i.url === '/company-knowledge');
    if (hasKnowledge) return items;
    return [
      ...items,
      // {
      //   title: 'Knowledge',
      //   url: '/company-knowledge',
      //   photo: '../assets/flaticons_white/insights.png'
      // }
    ];
  }

  public appPages2 = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      photo: '../assets/flaticons_white/dashboard.png',
    },
    {
      title: 'Interviews',
      url: '/job-offers',
      photo: '../assets/flaticons_white/loupe.png',
    },
    {
      title: 'Kandidaten',
      url: '/applicants',
      photo: '../assets/flaticons_white/candidates.png',
    },
    {
      title: 'Kommunikation',
      url: '/mona-communication-list',
      photo: '../assets/flaticons_white/user.png',
    },
    // {
    //   title: 'Teamverwaltung',
    //   url: '/team-management',
    //   photo: '../assets/flaticons_white/sales.png',
    // },
    // {
    //   title: 'Standort-Teams',
    //   url: '/location-teams',
    //   photo: '../assets/flaticons_white/manager.png',
    // },
    {
      title: 'SalesEmails',
      url: '/automations/email',
      photo: '../assets/flaticons/manager.png',
    },
  ];

  constructor() {
    // userRole is already initialized to 'ToBeSet' above
    // No need to call setMenuForRole here which would trigger subscriptions
  }

  // Trigger a Quick Wins guidance popup based on the current route and context
  public triggerQuickWinsPopupIfNeeded(url: string): void {
    try {
      const ctx = localStorage.getItem('quickWinsContext');
      if (!ctx) return;
      const u = url || '';
      let payload: { title: string; message: string } | null = null;
      if (u.indexOf('/agent-dashboard') !== -1 && ctx === 'start-chat') {
        payload = {
          title: 'Quick Wins',
          message: 'Starte dein erstes Gespräch: Oben links sollte bereits ein Agent ausgewählt sein. Gib unten deine erste Nachricht ein (z. B. "Erkläre mir die wichtigsten Funktionen").'
        };
      } else if (u.indexOf('/mona-agent-builder') !== -1 && ctx === 'create-agent') {
        payload = {
          title: 'Quick Wins',
          message: 'Erstelle deinen ersten Agenten: Klicke auf „Agent erstellen“, benenne ihn und speichere.'
        };
      } else if (u.indexOf('/email-inbox') !== -1 && ctx === 'connect-email') {
        payload = {
          title: 'Quick Wins',
          message: 'E-Mail verbinden: Generiere eine neue E-Mail oder hinterlege deine SMTP-Daten, um E-Mails zu automatisieren.'
        };
      } else if (u.indexOf('/sms-inbox') !== -1 && ctx === 'connect-sms') {
        payload = {
          title: 'Quick Wins',
          message: 'SMS verbinden: Aktiviere SMS-Versand und -Empfang, um Kandidaten schneller zu erreichen.'
        };
      } else if (u.indexOf('/workflow-builder') !== -1 && ctx === 'build-workflow') {
        payload = {
          title: 'Quick Wins',
          message: 'Ersten Workflow bauen: Starte mit einer Vorlage oder lege einen einfachen 3‑Schritt-Workflow an.'
        };
      } else if (u.indexOf('/company-knowledge') !== -1 && ctx === 'add-knowledge') {
        payload = {
          title: 'Quick Wins',
          message: 'Company Knowledge: Lade wichtige Dokumente hoch, damit deine Agenten sie kennen und nutzen können.'
        };
      } else if (u.indexOf('/integrations') !== -1 && ctx === 'integrations') {
        payload = {
          title: 'Quick Wins',
          message: 'Integrationen verbinden: Verbinde ATS/CRM oder Datenquellen, um Automatisierungen zu starten.'
        };
      }
      if (payload) {
        this.quickWinsGuideSource.next(payload);
        localStorage.removeItem('quickWinsContext');
      }
    } catch { }
  }

  public triggerOrganizationPopupIfNeeded(url: string, currentPlan: string): void {
    try {
      const u = url || '';
      const plan = currentPlan || '';
      const isEligible = plan.includes('agents-pro') || plan.includes('agents-ultra') || plan.includes('agents-enterprise');
      if (isEligible) return;

      let payload: { route: string; title: string; message: string } | null = null;
      if (u.indexOf('/email-inbox') !== -1) {
        payload = {
          route: '/email-inbox',
          title: 'Email Inbox',
          message: '<p>Alles in einem Posteingang für deine Kundenkommunikation.</p><ul><li>Mailbox verbinden (SMTP/IMAP oder generierte Adresse)</li><li>Sequenzen, Auto‑Replies und Regeln</li><li>KI‑Klassifizierung, Zuweisung, SLA</li><li>Trigger für Workflows & CRM‑Updates</li><li>Team‑Posteingang mit Mentions & Handovers</li></ul><p>Verfügbar ab agents‑pro.</p>'
        };
      } else if (u.indexOf('/sms-inbox') !== -1) {
        payload = {
          route: '/sms-inbox',
          title: 'SMS Inbox',
          message: '<p>Erreiche Kandidaten und Kunden per SMS – schnell und automatisiert.</p><ul><li>Empfangen & Senden inkl. Long Codes</li><li>Sequenzen und Follow‑ups</li><li>Opt‑in/Opt‑out‑Management</li><li>Workflows auslösen</li></ul><p>Verfügbar ab agents‑pro.</p>'
        };
      } else if (u.indexOf('/data-pipeline') !== -1) {
        payload = {
          route: '/data-pipeline',
          title: 'Data Pipeline',
          message: '<p>Verbinde Datenquellen und baue robuste ETL‑Pipelines.</p><ul><li>Connectors: CSV, Webhooks, APIs, Firebase/Firestore, SQL</li><li>Transformationen, Validierungen, Enrichment</li><li>Zeitpläne & Monitoring</li><li>Insights‑Dashboards</li></ul><p>Verfügbar ab agents‑pro.</p>'
        };
      } else if (u.indexOf('/workflow-builder') !== -1) {
        payload = {
          route: '/organigram',
          title: 'Process Manager',
          message: '<p>Visueller Process Manager für Ende‑zu‑Ende Automationen.</p><ul><li>Trigger (E‑Mail, Webhook, Zeitplan, Datenänderung)</li><li>Aktionen (Agenten, Nachrichten, Daten, Web‑Requests)</li><li>Bedingungen, Verzweigungen, Wiederholungen</li><li>Human‑in‑the‑loop Genehmigungen</li><li>Versionierung & Protokollierung</li></ul><p>Verfügbar ab agents‑pro.</p>'
        };
      } else if (u.indexOf('/database') !== -1) {
        payload = {
          route: '/database',
          title: 'Database',
          message: '<p>Leichte Datenbank für deine Workflows und Agenten.</p><ul><li>Tabellen & Beziehungen</li><li>Formulare & CRUD‑UI</li><li>Query‑Builder</li><li>Direkte Nutzung in Agenten & Workflows</li></ul><p>Verfügbar ab agents‑pro.</p>'
        };
      }
      if (payload) {
        this.organizationPopupSource.next(payload);
      }
    } catch { }
  }

  setPlanLimits(limits: any) {
    this.planLimits = limits;
    this.planLimits$.next(limits);
  }

  setCompanyLogo(logo: string) {
    this.menuCompanyLogo.next(logo);
  }

  // Dynamically add a menu item for sales only
  addMenuItem(newItem) {
    if (newItem?.url && this.hasMenuItemWithUrl(newItem.url)) return;
    this.menuItemsSource.next([...this.menuItemsSource.value, newItem]);
  }

  // Module Store items are user choices, so they survive role resets in setMenuForRole.
  addModuleMenuItem(item: MenuItem) {
    if (!item?.url) return;
    this.moduleItems.set(item.url, item);
    this.addMenuItem(item);
  }

  removeMenuItemByUrl(url: string) {
    this.moduleItems.delete(url);
    const filtered = this.menuItemsSource.value.filter(item => item.url !== url);
    this.menuItemsSource.next(filtered);
  }

  getMenuItems(): MenuItem[] {
    return this.menuItemsSource.value;
  }

  hasMenuItemWithUrl(url: string): boolean {
    return this.menuItemsSource.value.some(item => item.url === url);
  }

  // Set specific menu items
  setMenuItems(items: MenuItem[]) {
    this.menuItemsSource.next(items);
    // check if menu already active
  }

  setMenuItemsByUrl(urls: string[]) {
    const menuItems = this.enterpriseUserMenu.filter((menuItem) =>
      urls.includes(menuItem.url),
    );
    this.setMenuItems(menuItems);
  }

  setUserRoleByRole(role: number) {
    const roleObj = roles.find((roleItem) => roleItem.id === role);
    console.log('roleObj', roleObj);
    if (roleObj) {
      this.roleRestrictionUrls = roleObj.availableMenuPoints;
      this.applyRoleRestriction();
      if (roleObj.id != 5) {
        console.log('roleObj.id', roleObj.id);
        this.AccountSettings = [];
      }
    }
  }

  private applyRoleRestriction() {
    const allowed = this.roleRestrictionUrls;
    if (!allowed) return;
    const source = this.activeRoleMenu.length ? this.activeRoleMenu : this.enterpriseUserMenu;
    this.setMenuItems(
      source.filter(
        (item) => !MenuService.roleGovernedUrls.has(item.url) || allowed.includes(item.url),
      ),
    );
  }

  // Set the user role and corresponding menu
  setUserRole(role) {
    console.log('role set', role);
    this.setMenuForRole(role);
  }

  // Method to switch menu based on the user role
  public setMenuForRole(role) {
    console.log('role', role);

    // Safety check #1: Prevent too many updates in a row
    this.updateCounter++;
    if (this.updateCounter > this.MAX_UPDATE_DEPTH) {
      console.error('[MenuService] Maximum update depth reached! Preventing infinite loop. Role:', role);
      // Reset counter after 1 second to allow future legitimate updates
      setTimeout(() => {
        this.updateCounter = 0;
      }, 1000);
      return;
    }

    // Safety check #2: Only update if role actually changed
    if (this.userRole === role) {
      console.log('Role already set to', role, '- skipping update');
      this.updateCounter = 0; // Reset counter for legitimate calls
      return;
    }

    this.userRole = role;
    // this.userRole$.next(role);

    // Reset counter after successful update
    setTimeout(() => {
      this.updateCounter = 0;
    }, 100);
    // Reset to base items per role, without persistent ad-hoc items
    switch (role) {
      case 'pro':
        this.setMenuItems(this.freeUserMenu);
        break;
      case 'professional':
        this.setMenuItems(this.freeUserMenu);
        break;
      case 'agents':
        this.setMenuItems(this.withCompanyKnowledge(this.withIntegrations(this.agentsUserMenu)));
        break;
      case 'agents-free':
        this.setMenuItems(this.withOrganigramm(this.withIntegrations(this.agentsFreeMenu)));
        break;
      case 'agents-pro':
        this.setMenuItems(this.withOrganigramm(this.withIntegrations(this.agentsFreeMenu)));
        break;
      case 'agents-ultra':
        this.setMenuItems(this.withOrganigramm(this.withCompanyKnowledge(this.withIntegrations(this.agentsUserMenu))));
        break;
      case 'agents-enterprise':
        this.setMenuItems(this.withOrganigramm(this.withIntegrations(this.agentsFreeMenu)));
        break;
      case 'agents-enterprise-hybrid':
        this.setMenuItems(this.withOrganigramm(this.withIntegrations(this.agentsEnterpriseHybridMenu)));
        break;
      case 'hybrid':
        this.setMenuItems(this.hybridUserMenu);
        break;
      case 'enterprise':
        this.setMenuItems(this.enterpriseUserMenu);
        break;
      case 'enterprise-hybrid':
        this.setMenuItems(this.enterpriseHybridMenu);
        break;
      case 'self-service-recruiting':
        this.setMenuItems(this.selfserviceRecruitingMenu);
        break;
      case 'recruiting-pay-per-use-self-service':
        this.setMenuItems(this.selfserviceRecruitingMenu);
        break;
      case 'sales-pay-per-use-self-service':
        this.setMenuItems(this.selfserviceRecruitingMenu);
        break;
      case 'profile-pay-per-use-self-service':
        this.setMenuItems(this.selfserviceRecruitingMenu);
        break;
      default:
        this.setMenuItems(this.freeUserMenu); // Default to free menu
        break;
    }
    this.moduleItems.forEach(item => this.addMenuItem(item));
    this.activeRoleMenu = this.menuItemsSource.value;
    this.applyRoleRestriction();
  }

  public isMenuInitialized(): boolean {
    const items = this.menuItemsSource.value;
    return Array.isArray(items) && items.length > 0;
  }

  // Method to trigger menu state initialization in AppComponent
  public initializeMenuState() {
    // This will be called by the auth guard to ensure menu state is applied
    // We use a timeout to ensure DOM is ready
    setTimeout(() => {
      const event = new CustomEvent('menuStateInit');
      window.dispatchEvent(event);
    }, 100);
    setTimeout(() => {
      const event = new CustomEvent('menuStateInit');
      window.dispatchEvent(event);
    }, 500);
  }
}
